import { useCallback, useState } from "react";
import {
  View,
  Text,
  Image,
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  X,
  Stethoscope,
  Activity,
  Search,
  Heart,
  TrendingUp,
  Shield,
  Users,
  Venus,
  Mars,
  VenusAndMars,
  BookOpen,
  type LucideIcon,
} from "lucide-react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withTiming,
  runOnJS,
} from "react-native-reanimated";
import type { ContentBlock, ConditionBody } from "@src/packages/content";
import {
  useScrollHaptics,
  springs,
  SectionTitle,
  IconButton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../onboarding/theme";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { SideEffectTags } from "./SideEffectTags";
import LinkedFactSheetText, {
  type FactSheetLinkTarget,
} from "./LinkedFactSheetText";
import EvidenceLevelModal from "./EvidenceLevelModal";
import EvidenceSourceRows from "./EvidenceSourceRows";

// Maps the raw category value from CMS to a stable i18n key used by
// learn.encyclopedia_screens.conditions.categories.* (shared with ConditionsScreen).
const CATEGORY_I18N_KEYS: Record<string, string> = {
  reproductive: "reproductive",
  endocrine: "endocrine",
  autoimmune: "autoimmune",
  structural: "structural",
  genetic: "genetic",
  "male-factor": "male_factor",
};

const FOR_WHOM_I18N_KEYS: Record<string, string> = {
  her: "for_whom_her",
  him: "for_whom_him",
  both: "for_whom_both",
};

const FOR_WHOM_ICONS: Record<string, LucideIcon> = {
  her: Venus,
  him: Mars,
  both: VenusAndMars,
};

/* ─── Bullet list helper ─── */

function BulletList({
  items,
  currentBlockId,
  onOpenFactSheet,
}: {
  items: string[];
  currentBlockId: string;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  return (
    <View style={{ marginTop: 10, gap: 6 }}>
      {items.map((s, i) => (
        <View key={i} style={{ flexDirection: "row", gap: 8 }}>
          <Text
            style={{
              fontSize: 14,
              color: EMBER.colors.textFaint,
              lineHeight: 22,
            }}
          >
            •
          </Text>
          <LinkedFactSheetText
            text={s}
            currentBlockId={currentBlockId}
            onOpenLink={onOpenFactSheet}
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              lineHeight: 22,
              flex: 1,
            }}
          />
        </View>
      ))}
    </View>
  );
}

/* ─── Detail Content ─── */

export function ConditionDetailContent({
  item,
  onClose,
  skipExitAnimation,
  onOpenFactSheet,
}: {
  item: ContentBlock<"condition">;
  onClose: () => void;
  skipExitAnimation?: boolean;
  onOpenFactSheet?: (target: FactSheetLinkTarget) => void;
}) {
  const t = useTranslations();
  const insets = useSafeAreaInsets();
  const body = item.body as ConditionBody;
  const scale = useSharedValue(0.92);
  const opacity = useSharedValue(0);
  const onScrollHaptics = useScrollHaptics();
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);

  useState(() => {
    scale.value = withSpring(1, springs.sheet);
    opacity.value = withTiming(1, { duration: 250 });
  });

  const animatedStyle = useAnimatedStyle(() => ({
    flex: 1,
    overflow: "hidden" as const,
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  const handleClose = useCallback(() => {
    if (skipExitAnimation) {
      onClose();
      return;
    }
    scale.value = withSpring(0.92, springs.sheet);
    opacity.value = withTiming(0, { duration: 200 }, () => {
      runOnJS(onClose)();
    });
  }, [scale, opacity, onClose, skipExitAnimation]);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScrollHaptics(e);
    },
    [onScrollHaptics],
  );

  // Badge chips
  const badges: {
    label?: string;
    icon?: LucideIcon;
    accessibilityLabel?: string;
  }[] = [];
  const categoryKey = CATEGORY_I18N_KEYS[body.category];
  badges.push({
    label: categoryKey
      ? t(`learn.encyclopedia_screens.conditions.categories.${categoryKey}`)
      : body.category,
  });
  if (body.forWhom && body.forWhom !== "both") {
    const forWhomKey = FOR_WHOM_I18N_KEYS[body.forWhom];
    const accessibilityLabel = forWhomKey
      ? t(`learn.condition_detail.${forWhomKey}`)
      : body.forWhom;
    badges.push({
      icon: FOR_WHOM_ICONS[body.forWhom],
      accessibilityLabel,
    });
  }

  return (
    <View style={{ flex: 1, overflow: "hidden" }}>
      <Image
        source={require("@louise/moon-ui/assets/images/onboarding-bg.jpg")}
        style={{
          position: "absolute",
          width: "115%",
          height: "115%",
          left: "-7.5%",
          top: "-7.5%",
        }}
        resizeMode="cover"
      />
      <Animated.View style={animatedStyle}>
        <View style={{ flex: 1, paddingTop: insets.top }}>
          <Animated.ScrollView
            contentContainerStyle={{
              paddingHorizontal: 16,
              paddingTop: SCREEN_PADDING_TOP,
              paddingBottom: 100,
              maxWidth: 520,
              width: "100%",
              alignSelf: "center",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            <View
              style={{
                ...GLASS_CARD_STYLE,
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
              }}
            >
              {/* Close */}
              <View
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  zIndex: 2,
                }}
              >
                <IconButton
                  onPress={handleClose}
                  hitSlop={8}
                  size={32}
                  surface="soft"
                  backgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedBackgroundColor="rgba(0, 0, 0, 0.05)"
                  pressedOpacity={1}
                  accessibilityLabel="Close"
                >
                  <X size={16} color={EMBER.colors.textLight} />
                </IconButton>
              </View>

              {/* Icon */}
              <View
                style={{
                  alignItems: "center",
                  paddingTop: 8,
                  paddingBottom: 20,
                }}
              >
                <View
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 32,
                    backgroundColor: "rgba(107, 85, 72, 0.08)",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Stethoscope size={28} color="#6B5548" strokeWidth={1.5} />
                </View>
              </View>

              {/* Title — centered */}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 22,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                  textAlign: "center",
                  lineHeight: 28,
                }}
              >
                {item.title}
              </Text>

              {/* Badge row */}
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 6,
                  marginTop: 12,
                }}
              >
                {badges.map((badge, index) => {
                  const Icon = badge.icon;
                  return (
                    <View
                      key={badge.label ?? badge.accessibilityLabel ?? index}
                      accessibilityLabel={
                        badge.accessibilityLabel ?? badge.label
                      }
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: Icon && badge.label ? 5 : 4,
                        paddingHorizontal: badge.label ? 10 : 8,
                        paddingVertical: 5,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: "rgba(0,0,0,0.08)",
                        backgroundColor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      {Icon ? (
                        <Icon
                          size={11}
                          color={EMBER.colors.textFaint}
                          strokeWidth={1.7}
                        />
                      ) : null}
                      {badge.label ? (
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 11,
                            fontWeight: "500",
                            color: EMBER.colors.textMid,
                          }}
                        >
                          {badge.label}
                        </Text>
                      ) : null}
                    </View>
                  );
                })}
              </View>

              {/* ── What is it ── */}
              <View style={{ marginTop: 28 }}>
                <SectionTitle icon={Heart}>
                  {t("learn.condition_detail.section_what_is_it")}
                </SectionTitle>
                <LinkedFactSheetText
                  text={body.description}
                  currentBlockId={item.id}
                  onOpenLink={onOpenFactSheet}
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textLight,
                    lineHeight: 22,
                    marginTop: 10,
                  }}
                />
              </View>

              {/* ── Symptoms ── */}
              {body.symptoms.length > 0 && (
                <View style={{ marginTop: 24 }}>
                  <SectionTitle icon={Activity}>
                    {t("learn.condition_detail.section_symptoms")}
                  </SectionTitle>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      color: EMBER.colors.textLight,
                      lineHeight: 18,
                      marginTop: 10,
                      marginBottom: 10,
                    }}
                  >
                    {t("learn.condition_detail.symptoms_log_hint")}
                  </Text>
                  <SideEffectTags symptoms={body.symptoms} />
                </View>
              )}

              {/* ── How it's diagnosed ── */}
              <View style={{ marginTop: 24 }}>
                <SectionTitle icon={Search}>
                  {t("learn.condition_detail.section_how_diagnosed")}
                </SectionTitle>
                <LinkedFactSheetText
                  text={body.howDiagnosed}
                  currentBlockId={item.id}
                  onOpenLink={onOpenFactSheet}
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textLight,
                    lineHeight: 22,
                    marginTop: 10,
                  }}
                />
              </View>

              {/* ── Impact on fertility ── */}
              <View style={{ marginTop: 24 }}>
                <SectionTitle icon={TrendingUp}>
                  {t("learn.condition_detail.section_impact_on_fertility")}
                </SectionTitle>
                <LinkedFactSheetText
                  text={body.impactOnFertility}
                  currentBlockId={item.id}
                  onOpenLink={onOpenFactSheet}
                  style={{
                    fontSize: 14,
                    color: EMBER.colors.textLight,
                    lineHeight: 22,
                    marginTop: 10,
                  }}
                />
              </View>

              {/* ── Prevalence ── */}
              {body.prevalence && (
                <View style={{ marginTop: 24 }}>
                  <SectionTitle icon={Users}>
                    {t("learn.condition_detail.section_prevalence")}
                  </SectionTitle>
                  <LinkedFactSheetText
                    text={body.prevalence}
                    currentBlockId={item.id}
                    onOpenLink={onOpenFactSheet}
                    style={{
                      fontSize: 14,
                      color: EMBER.colors.textLight,
                      lineHeight: 22,
                      marginTop: 10,
                    }}
                  />
                </View>
              )}

              {/* ── Treatment options ── */}
              {body.treatmentOptions && body.treatmentOptions.length > 0 && (
                <View style={{ marginTop: 24 }}>
                  <SectionTitle icon={Shield}>
                    {t("learn.condition_detail.section_treatment_options")}
                  </SectionTitle>
                  <BulletList
                    items={body.treatmentOptions}
                    currentBlockId={item.id}
                    onOpenFactSheet={onOpenFactSheet}
                  />
                </View>
              )}

              {/* ── Prognosis ── */}
              {body.prognosis && (
                <View style={{ marginTop: 24 }}>
                  <SectionTitle icon={TrendingUp}>
                    {t("learn.condition_detail.section_prognosis")}
                  </SectionTitle>
                  <LinkedFactSheetText
                    text={body.prognosis}
                    currentBlockId={item.id}
                    onOpenLink={onOpenFactSheet}
                    style={{
                      fontSize: 14,
                      color: EMBER.colors.textLight,
                      lineHeight: 22,
                      marginTop: 10,
                    }}
                  />
                </View>
              )}

              {/* ── Evidence ── */}
              {item.sources?.length ? (
                <View style={{ marginTop: 28 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("condition_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={item.sources}
                    onEvidenceLevelPress={setEvidenceModal}
                    style={{ marginTop: 10 }}
                  />
                </View>
              ) : null}

              {/* ── Disclaimer ── */}
              <View
                style={{
                  marginTop: 32,
                  padding: 14,
                  borderRadius: 12,
                  backgroundColor: "rgba(0,0,0,0.03)",
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 11,
                    color: EMBER.colors.textFaint,
                    lineHeight: 17,
                    textAlign: "center",
                  }}
                >
                  {t("learn.disclaimer_medical_info")}
                </Text>
              </View>
            </View>
          </Animated.ScrollView>
        </View>
      </Animated.View>

      <EvidenceLevelModal
        level={evidenceModal}
        visible={evidenceModal !== null}
        onClose={() => setEvidenceModal(null)}
      />
    </View>
  );
}

/* ─── Modal Wrapper ─── */

type Props = {
  item: ContentBlock<"condition"> | null;
  onClose?: () => void;
};

export default function ConditionDetailSheet({ item, onClose }: Props) {
  if (!item) return null;

  const handleClose = onClose ?? (() => {});

  return (
    <Modal
      visible
      transparent
      animationType="none"
      onRequestClose={handleClose}
    >
      <ConditionDetailContent item={item} onClose={handleClose} />
    </Modal>
  );
}
